import { useEffect, useRef, useState, type FormEvent } from "react";
import { takeDestination } from "@/shared/lib/pendingDestination";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { AuthShell } from "@/shared/components/auth/AuthShell";
import { ac } from "@/shared/components/auth/authCopy";
import {
  PENDING_VERTICAL_KEY,
  PENDING_DOMAIN_KEY,
  checkVerticalAccess,
  provisionVertical,
  type VerticalAccessResult,
} from "@/shared/lib/verticalClient";
import { verticalLabel } from "@/shared/lib/verticalLabels";
import { resolveProductIntent } from "@/shared/lib/productIntent";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAVY = "#0c1a33";

type Locale = "en" | "uk" | "pl" | "ro";
type Step = "email" | "password";
type MatchedOrg = NonNullable<VerticalAccessResult["org"]>;

const COPY = {
  stepEmail: { en: "Email", uk: "Email", pl: "Email", ro: "Email" },
  stepPassword: { en: "Password", uk: "Пароль", pl: "Hasło", ro: "Parolă" },
  emailTitle: {
    en: "What’s your email?",
    uk: "Яка ваша електронна адреса?",
    pl: "Jaki jest Twój adres e-mail?",
    ro: "Care este adresa ta de email?",
  },
  emailSubtitle: {
    en: "Use your personal or work email to start practicing.",
    uk: "Використайте особисту або робочу адресу, щоб почати практику.",
    pl: "Użyj prywatnego lub służbowego adresu e-mail, aby rozpocząć praktykę.",
    ro: "Folosește adresa personală sau profesională de email pentru a începe practica.",
  },
  passwordTitle: {
    en: "Create a password",
    uk: "Створіть пароль",
    pl: "Utwórz hasło",
    ro: "Creează o parolă",
  },
  passwordSubtitle: {
    en: "One last step. Use at least 6 characters.",
    uk: "Останній крок. Використайте щонайменше 6 символів.",
    pl: "Ostatni krok. Użyj co najmniej 6 znaków.",
    ro: "Ultimul pas. Folosește cel puțin 6 caractere.",
  },
  continue: {
    en: "Continue",
    uk: "Продовжити",
    pl: "Kontynuuj",
    ro: "Continuă",
  },
  back: { en: "Back", uk: "Назад", pl: "Wstecz", ro: "Înapoi" },
  checking: {
    en: "Checking…",
    uk: "Перевіряємо…",
    pl: "Sprawdzanie…",
    ro: "Se verifică…",
  },
  emailError: {
    en: "Enter a valid email address.",
    uk: "Введіть дійсну електронну адресу.",
    pl: "Wpisz prawidłowy adres e-mail.",
    ro: "Introdu o adresă de email validă.",
  },
  setupError: {
    en: "Your account was created, but setup could not be completed. Sign in to finish setup.",
    uk: "Акаунт створено, але налаштування не завершено. Увійдіть, щоб завершити його.",
    pl: "Konto zostało utworzone, ale konfiguracja nie została ukończona. Zaloguj się, aby ją dokończyć.",
    ro: "Contul a fost creat, dar configurarea nu a fost finalizată. Autentifică-te pentru a o încheia.",
  },
  workspaceReady: {
    en: "You're set up on {vertical}.",
    uk: "Ваш акаунт налаштовано на {vertical}.",
    pl: "Twoje konto jest skonfigurowane w {vertical}.",
    ro: "Contul tău este configurat pe {vertical}.",
  },
  pendingAccessNote: {
    en: "We couldn't confirm your organization's access yet, so you're using {vertical} for now. We've noted your request and will follow up once it's verified.",
    uk: "Ми поки не змогли підтвердити доступ вашої організації, тож наразі ви користуєтесь {vertical}. Ми зафіксували запит і зв’яжемося, щойно його підтвердять.",
    pl: "Nie udało się jeszcze potwierdzić dostępu Twojej organizacji, więc na razie korzystasz z {vertical}. Zapisaliśmy Twoją prośbę i skontaktujemy się po jej zweryfikowaniu.",
    ro: "Nu am putut confirma încă accesul organizației tale, așa că folosești deocamdată {vertical}. Am notat cererea ta și vom reveni după verificare.",
  },
};

const ACCESS_INCLUDED: Record<Locale, string> = {
  en: "access included",
  uk: "доступ включено",
  pl: "dostęp w zestawie",
  ro: "acces inclus",
};

/** Maps a marketing vertical slug (from a product/branch page "Sign up" CTA,
 * `?vertical=`) to the vertical the account should be provisioned on, and to the
 * AuthShell brand theme. Only names verticals with real AuthShell brand content
 * (BRANCHES); unknown params fall through to the neutral theme + "general". */
const ENTRY_VERTICAL: Partial<Record<string, string>> = {
  "crisis-negotiation": "crisis-negotiation",
  "mental-health": "mental-health",
};

function readEntryVertical(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const param = new URLSearchParams(window.location.search).get("vertical");
    return param ? (ENTRY_VERTICAL[param] ?? null) : null;
  } catch {
    return null;
  }
}

const STEP_ORDER: Step[] = ["email", "password"];

export function SignupPage() {
  const { signUp, signInWithGoogle, user } = useAuth();
  const { t, locale } = useLanguage();
  const L = (locale as Locale) || "en";
  const tx = (value: Record<Locale, string>) => value[L] ?? value.en;

  // Product front door: its vertical
  // is the provisioning default and the rail theme (a matched org + the server
  // gate still win).
  const [productIntent] = useState(resolveProductIntent);
  const defaultDestination = "/dashboard";
  const loginPath = productIntent ? `/${productIntent.id}/login` : "/login";
  const [entryVertical] = useState(readEntryVertical);
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [provisionResult, setProvisionResult] = useState<{
    ok: boolean;
    vertical?: string;
    status?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [matchedOrg, setMatchedOrg] = useState<MatchedOrg | null>(null);
  const lastChecked = useRef<string>("");

  // The vertical a new account lands on: the matched org's wins, else the entry
  // CTA's vertical, else the general default. Generic signup intentionally
  // leaves optional role-specific preferences unset.
  const chosenVertical = (org?: { vertical: string } | null): string =>
    org?.vertical ?? productIntent?.vertical ?? entryVertical ?? "general";
  const themeVertical =
    matchedOrg?.vertical ??
    productIntent?.vertical ??
    entryVertical ??
    undefined;
  const activeStep = STEP_ORDER.indexOf(step);

  useEffect(() => {
    if (!user) return;
    let redirectTo = takeDestination() ?? defaultDestination;
    try {
      const stored = sessionStorage.getItem("loginRedirectTo");
      if (stored) {
        redirectTo = stored;
        sessionStorage.removeItem("loginRedirectTo");
      }
    } catch {
      /* ignore */
    }
    (window as any).navigate(redirectTo);
  }, [defaultDestination, user]);

  const runGate = async (raw: string): Promise<MatchedOrg | null> => {
    const normalized = raw.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) return null;
    if (normalized === lastChecked.current) return matchedOrg;
    lastChecked.current = normalized;
    const result = await checkVerticalAccess(normalized);
    const org = result.org ?? null;
    setMatchedOrg(org);
    return org;
  };

  const goToStep = (next: Step) => {
    setError(null);
    setStep(next);
  };

  const handleEmailContinue = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) {
      setError(tx(COPY.emailError));
      return;
    }
    setCheckingEmail(true);
    setError(null);
    await runGate(normalized);
    setEmail(normalized);
    setCheckingEmail(false);
    setStep("password");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError(t.auth.passwordMismatch);
      return;
    }
    if (password.length < 6) {
      setError(t.auth.passwordTooShort);
      return;
    }

    setLoading(true);
    const access = await checkVerticalAccess(email);
    setMatchedOrg(access.org ?? null);

    const { error: signupError } = await signUp(email, password);
    if (signupError) {
      setError(signupError.message);
      setLoading(false);
      return;
    }

    const provisioned = await provisionVertical(
      email,
      chosenVertical(access.org),
      undefined,
      productIntent?.domain,
    );
    setLoading(false);
    if (!provisioned.ok) {
      setError(tx(COPY.setupError));
      return;
    }
    setProvisionResult(provisioned);
    setSuccess(true);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      localStorage.setItem(PENDING_VERTICAL_KEY, chosenVertical(matchedOrg));
      if (productIntent?.domain)
        localStorage.setItem(PENDING_DOMAIN_KEY, productIntent.domain);
    } catch {
      /* ignore */
    }
    const { error: googleError } = await signInWithGoogle();
    if (googleError) setError(googleError.message);
  };

  const labelCls = "au-mono mb-1.5 block text-[0.625rem]";
  const inputCls =
    "min-h-12 w-full rounded-[3px] border border-[rgba(12,26,51,0.18)] bg-white px-3.5 text-base text-[#0c1a33] placeholder-[rgba(12,26,51,0.35)] outline-none transition-all focus:border-[var(--auth-accent)] focus:ring-[3px] focus:ring-[var(--auth-accent-soft)]";
  const accentBtn =
    "inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-[3px] bg-[var(--auth-accent)] px-4 text-[0.82rem] font-semibold tracking-wide text-white transition-colors hover:brightness-110 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100";
  const secondaryBtn =
    "inline-flex min-h-12 items-center justify-center gap-2 rounded-[3px] border border-[rgba(12,26,51,0.18)] bg-white px-4 text-[0.82rem] font-semibold text-[#0c1a33] transition-colors hover:bg-[rgba(12,26,51,0.04)] cursor-pointer";

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafaf8] px-6">
        <div className="w-full max-w-sm text-center">
          <div
            className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full"
            style={{
              background: "#fff",
              boxShadow: "inset 0 0 0 1px rgba(12,26,51,0.14)",
            }}
          >
            <svg
              className="h-6 w-6"
              style={{ color: NAVY }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2
            className="au-display mb-2 text-xl font-semibold"
            style={{ color: NAVY }}
          >
            {t.auth.checkEmail}
          </h2>
          <p className="mb-3 text-sm" style={{ color: "rgba(12,26,51,0.55)" }}>
            {t.auth.confirmationSent}
          </p>
          {provisionResult?.vertical && (
            <p
              className="mb-6 text-xs leading-relaxed"
              style={{ color: "rgba(12,26,51,0.55)" }}
            >
              {(provisionResult.status === "pending_access"
                ? tx(COPY.pendingAccessNote)
                : tx(COPY.workspaceReady)
              ).replace(
                "{vertical}",
                verticalLabel(provisionResult.vertical, locale),
              )}
            </p>
          )}
          <button
            type="button"
            onClick={() => (window as any).navigate(loginPath)}
            className="au-mono min-h-11 cursor-pointer text-[0.625rem]"
            style={{ color: NAVY }}
          >
            {t.auth.backToLogin}
          </button>
        </div>
      </div>
    );
  }

  const title = step === "email" ? tx(COPY.emailTitle) : tx(COPY.passwordTitle);
  const subtitle =
    step === "email" ? tx(COPY.emailSubtitle) : tx(COPY.passwordSubtitle);

  return (
    <AuthShell
      locale={locale}
      vertical={themeVertical}
      org={matchedOrg}
      product={
        !matchedOrg && productIntent
          ? { accent: productIntent.accent, name: productIntent.label }
          : undefined
      }
      railTitle={
        !matchedOrg && productIntent
          ? productIntent.tagline
          : ac("signupTitle", locale)
      }
      railFooter={ac("trust", locale)}
      compactMobile
    >
      <ol className="mb-6 grid grid-cols-2 gap-2" aria-label="Signup progress">
        {[COPY.stepEmail, COPY.stepPassword].map((label, index) => {
          const complete = index < activeStep;
          const current = index === activeStep;
          return (
            <li key={label.en} aria-current={current ? "step" : undefined}>
              <span
                className={`mb-2 block h-1 rounded-full transition-colors ${complete || current ? "bg-[var(--auth-accent)]" : "bg-[rgba(12,26,51,0.12)]"}`}
                aria-hidden="true"
              />
              <span
                className={`au-mono text-[0.55rem] ${current ? "text-[#0c1a33]" : "text-[rgba(12,26,51,0.42)]"}`}
              >
                {tx(label)}
              </span>
            </li>
          );
        })}
      </ol>

      <h1
        className="au-display mb-1 text-2xl font-semibold"
        style={{ color: NAVY }}
      >
        {title}
      </h1>
      <p
        className="mb-6 text-sm leading-relaxed"
        style={{ color: "rgba(12,26,51,0.58)" }}
      >
        {subtitle}
      </p>

      {step === "email" && (
        <>
          <form onSubmit={handleEmailContinue} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className={labelCls}
                style={{ color: "rgba(12,26,51,0.62)" }}
              >
                {t.auth.email}
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setMatchedOrg(null);
                  lastChecked.current = "";
                }}
                required
                autoFocus
                className={inputCls}
              />
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-[3px] border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={checkingEmail}
              className={accentBtn}
            >
              {checkingEmail ? tx(COPY.checking) : tx(COPY.continue)}
              {!checkingEmail && <ArrowRight size={15} aria-hidden="true" />}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-[rgba(12,26,51,0.12)]" />
            <span className="au-mono text-[0.5625rem] text-[rgba(12,26,51,0.4)]">
              {t.auth.or}
            </span>
            <div className="h-px flex-1 bg-[rgba(12,26,51,0.12)]" />
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="flex min-h-12 w-full items-center justify-center gap-3 rounded-[3px] border border-[rgba(12,26,51,0.18)] bg-white px-4 text-[0.85rem] font-medium text-[#0c1a33] transition-colors hover:bg-[rgba(12,26,51,0.03)] cursor-pointer"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t.auth.continueWithGoogle}
          </button>
        </>
      )}

      {step === "password" && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-[3px] border border-[rgba(12,26,51,0.12)] bg-white px-3.5 py-3">
            <span className="au-mono block text-[0.55rem] text-[rgba(12,26,51,0.46)]">
              {t.auth.email}
            </span>
            <span className="mt-1 block truncate text-sm font-medium text-[#0c1a33]">
              {email}
            </span>
          </div>

          {matchedOrg && (
            <div className="flex items-start gap-2 rounded-[3px] border border-[var(--auth-accent)] bg-[var(--auth-accent-soft)] px-3 py-2.5">
              <span
                aria-hidden="true"
                className="mt-px font-semibold text-[var(--auth-accent)]"
              >
                ✓
              </span>
              <span className="text-[0.72rem] leading-snug text-[#0c1a33]">
                <span className="font-semibold">
                  {matchedOrg.branding?.brandName ?? matchedOrg.name}
                </span>
                {" — "}
                {verticalLabel(matchedOrg.vertical, locale)}{" "}
                {ACCESS_INCLUDED[L]}
              </span>
            </div>
          )}

          <div>
            <label
              htmlFor="password"
              className={labelCls}
              style={{ color: "rgba(12,26,51,0.62)" }}
            >
              {t.auth.password}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={6}
              autoFocus
              className={inputCls}
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className={labelCls}
              style={{ color: "rgba(12,26,51,0.62)" }}
            >
              {t.auth.confirmPassword}
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              minLength={6}
              className={inputCls}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-[3px] border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="grid grid-cols-[auto_1fr] gap-2">
            <button
              type="button"
              onClick={() => goToStep("email")}
              className={secondaryBtn}
              aria-label={tx(COPY.back)}
            >
              <ArrowLeft size={15} aria-hidden="true" />
            </button>
            <button type="submit" disabled={loading} className={accentBtn}>
              {loading ? t.auth.creatingAccount : t.auth.createAccount}
              {!loading && <ArrowRight size={15} aria-hidden="true" />}
            </button>
          </div>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-[rgba(12,26,51,0.56)]">
        {t.auth.haveAccount}{" "}
        <button
          type="button"
          onClick={() => (window as any).navigate(loginPath)}
          className="min-h-11 font-medium text-[var(--auth-accent)] cursor-pointer"
        >
          {t.auth.signInLink}
        </button>
      </p>
    </AuthShell>
  );
}
